import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { postRequestAuthenticated } from "../../../services/rest";
import { privatePollResponse } from "../../../redux/slices/pollSlice";

// Renders a poll that lives inside the chat stream (ChannelChat.poll). Fetches
// the current tally on mount, lets the member vote once, and shows results
// according to the poll's showResults policy.
const PollCard = ({ poll, color = "bg-theme-tertiaryBackground" }) => {
  const dispatch = useDispatch();
  const myData = useSelector((state) => state.myData);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  const [voteCounts, setVoteCounts] = useState({});
  const [userChoice, setUserChoice] = useState(null);
  const [userResponded, setUserResponded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isClosed, setIsClosed] = useState(!!poll?.isClosed);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState("");

  const pollId = poll?._id;
  const choices = poll?.choices || [];
  const showResults = poll?.showResults || "afterVote";

  useEffect(() => {
    let active = true;
    if (!pollId) return;
    postRequestAuthenticated("/fetch/poll/data", {
      pollId,
      userId: myData?._id,
    })
      .then((res) => {
        if (!active || !res?.success) return;
        const r = res.response || {};
        setVoteCounts(r.voteCounts || {});
        setUserChoice(r.userChoice || null);
        setUserResponded(!!r.userResponded);
        if (res.poll) {
          setIsAdmin(!!res.poll.isAdmin);
          if (typeof res.poll.isClosed === "boolean")
            setIsClosed(res.poll.isClosed);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pollId, myData?._id]);

  const total = Object.values(voteCounts).reduce(
    (sum, c) => sum + (parseInt(c, 10) || 0),
    0
  );
  const canSeeResults =
    showResults === "always" ||
    (showResults === "afterVote" && userResponded) ||
    (showResults === "adminOnly" && isAdmin);

  const handleVote = (choice) => {
    if (voting || userResponded || isClosed) return;
    if (!isLoggedIn) {
      setError("Sign in to vote.");
      return;
    }
    setError("");
    setVoting(true);
    dispatch(privatePollResponse({ choice, pollId }))
      .unwrap()
      .then((res) => {
        // pollSlice returns response.poll on success; the controller payload is
        // { pollId, voteCounts, userResponded, userChoice } under `response`.
        const r = res?.response || res || {};
        if (r.voteCounts) setVoteCounts(r.voteCounts);
        setUserChoice(r.userChoice || choice);
        setUserResponded(true);
      })
      .catch((err) => {
        setError(typeof err === "string" ? err : err?.message || "Could not vote.");
      })
      .finally(() => setVoting(false));
  };

  if (!poll) return null;

  return (
    <div
      className={`${color} mt-2 w-max max-w-xs rounded-lg border border-theme-chatDivider p-3 sm:max-w-sm`}
    >
      <p className="mb-2 text-sm font-medium text-theme-secondaryText">
        {poll.question}
      </p>
      <div className="flex flex-col gap-2">
        {choices.map((choice, i) => {
          const count = parseInt(voteCounts[choice], 10) || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const isMine = userChoice === choice;
          const votable = !userResponded && !isClosed;
          return (
            <button
              key={i}
              type="button"
              onClick={() => handleVote(choice)}
              disabled={!votable || voting}
              className={`relative overflow-hidden rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                isMine
                  ? "border-theme-accent text-theme-secondaryText"
                  : "border-theme-chatDivider text-theme-primaryText"
              } ${votable ? "cursor-pointer hover:bg-theme-sidebarHighlight" : "cursor-default"}`}
            >
              {canSeeResults && (
                <span
                  className="absolute inset-y-0 left-0 bg-theme-accentSoft"
                  style={{ width: `${pct}%` }}
                  aria-hidden="true"
                />
              )}
              <span className="relative flex items-center justify-between gap-3">
                <span className="truncate">
                  {choice}
                  {isMine && " ✓"}
                </span>
                {canSeeResults && (
                  <span className="flex-shrink-0 text-xs text-theme-placeholder">
                    {pct}% · {count}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-theme-placeholder">
        <span>
          {total} {total === 1 ? "vote" : "votes"}
          {isClosed && " · closed"}
        </span>
        {!canSeeResults && !userResponded && !isClosed && (
          <span>Vote to see results</span>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-theme-error">{error}</p>}
    </div>
  );
};

export default PollCard;
